import type { Location } from "react-router-dom";

/** SessionStorage key: set when leaving `/edit-profile` after a successful save; consumed once by the profile modal provider. */
export const OPEN_OWN_PROFILE_AFTER_EDIT_SAVE_STORAGE_KEY = "c8_open_own_profile_after_edit_save";

/** Pass as route `state` when linking to `/profile` so the modal can return to the previous screen. */
export type ProfileModalLocationState = {
  from?: Pick<Location, "pathname" | "search">;
};

export type CloseProfileModalDetail = {
  /**
   * Run after the profile overlay has closed and any `history.go` has settled.
   * Required when opening another history-backed overlay (shop, wallet, my games):
   * closing the profile with `history.go(-N)` in the same turn pops the new overlay’s
   * dummy history entry and immediately dismisses it.
   */
  afterClose?: () => void;
};

export function profileModalBackgroundState(location: Location): ProfileModalLocationState {
  return { from: { pathname: location.pathname, search: location.search } };
}

/**
 * Close the global `/profile` modal (if open), then run `cb` after history has settled.
 * Prefer this over firing `closeProfileModal` and opening another overlay in the same turn.
 */
export function closeProfileModalThen(cb: () => void): void {
  if (typeof window === "undefined") {
    cb();
    return;
  }
  window.dispatchEvent(
    new CustomEvent<CloseProfileModalDetail>("closeProfileModal", {
      detail: { afterClose: cb },
    })
  );
}

export function isProfileHref(href: string): boolean {
  const path = href.split("?")[0] || "";
  return path === "/profile";
}

/** Parse `/profile` and query (p, u, edit). Used to open the global profile modal without routing. */
export function parseProfileHref(
  href: string
): { type: "own"; openEdit?: boolean } | { type: "public"; p?: string; u?: string } | null {
  try {
    const pathOnly = href.split("?")[0] || "";
    if (pathOnly !== "/profile") return null;
    const qs = href.includes("?") ? href.slice(href.indexOf("?") + 1) : "";
    const params = new URLSearchParams(qs);
    const p = params.get("p")?.trim() || undefined;
    const u = params.get("u")?.trim() || undefined;
    const openEdit = params.get("edit") === "1";
    if (p || u) return { type: "public", p, u };
    return { type: "own", openEdit: openEdit || undefined };
  } catch {
    return null;
  }
}
